// Drugs' receptor map on its own (ba-web): the Receptors section's Show control draws the chosen target's density on the
// cortex in place of the course's engagement (occupancy × density). `density` is Hansen 2022's PET parcel means on
// Schaefer-400, the cortex pack's `receptor-<target>` table read through its `schaefer400` labels (relative 0–1 within the
// target); `pet` is the vertex-wise group PET map where the pack has one (`pharma-pet-<target>`, Bmax in pmol/mL). Both go
// through the live vertex layer (viewport-api-v0) on the cortex's own vertices, left then right; the newest layer with
// data takes the field, so the engagement tint rests while a map is shown and returns when the layer is removed. The 2D
// preview has no setVertexData: status 'none', and the section says so.
//
// The layers are read from the pack the viewport draws as the cortex (the store's `brain.pack`: BigBrain's HD cortex on a
// node that has it, else the template's), in that pack's own vertex order: `space: 'own'` means the drawn mesh's vertices,
// and the two packs' fs_LR 164k surfaces are ordered differently (their Schaefer labels agree by index 1.3 % of the time,
// chance). Both carry the same `receptor-*` and `pharma-pet-*` layers. Read from a fixed pack id, the template's order laid
// over BigBrain's mesh drew as a fine stipple (2026-10-03).
import { useEffect } from 'react';
import { create } from 'zustand';
import { useAtlas } from '../app/store.ts';
import { stillView } from '../app/scene.ts';
import { packSource } from '../data/sims/crosswalk.ts';
import { SimFiles, type SimSource } from '../data/sims/files.ts';
import { decode, parseDType } from '../data/sims/binary.ts';
import { getViewport } from '../viewport/host.ts';
import type { Command } from '../viewport/api.ts';

export type DrugShow = 'engagement' | 'density' | 'pet';
export type MapShow = Exclude<DrugShow, 'engagement'>;
export const isShow = (v: unknown): v is DrugShow => v === 'engagement' || v === 'density' || v === 'pet';
/** The layer's id in the viewport. */
export const DENSITY_LAYER = 'drugs:density';

/** The pack layer a target's map is, by the pack's naming: `receptor-5ht2a` (Hansen 2022's parcel table), `pharma-pet-5ht2a` (the vertex-wise PET map). */
export const layerFor = (target: string, show: MapShow): string => `${show === 'density' ? 'receptor' : 'pharma-pet'}-${target.toLowerCase()}`;

export interface DensityMap {
  /** The pack the layer was read from: the cortex the viewport draws. */
  pack: string;
  layer: string;
  show: MapShow;
  target: string;
  /** One value a vertex, the left hemisphere then the right; NaN where the map has none. */
  values: Float32Array;
  range: [number, number];
  units: string;
  method: string;
  provenance: string;
  /** Vertices with a value, of all. */
  finite: number;
  vertices: number;
}

/** A map the pack doesn't have for the target: the section says so, no error. */
export class NoMap extends Error {}

interface LayerEntry {
  name: string;
  kind: string;
  dtype: string;
  file: string;
  range?: [number, number];
  units?: string;
  nodata?: unknown;
  label_layer?: string;
  index?: string;
  provenance?: string;
  method?: string;
}
interface LayerIndex {
  vertices?: number;
  layers?: LayerEntry[];
}

/** A parcel table spread over the vertices through its label layer (`index: 'label-1'`: row = label − 1). No label, or a row the table lacks, is NaN. */
export function expandParcels(table: ArrayLike<number>, labels: ArrayLike<number>, nodata = 0): Float32Array {
  const out = new Float32Array(labels.length);
  for (let i = 0; i < labels.length; i++) {
    const l = labels[i]!;
    const row = l - 1;
    out[i] = l === nodata || row < 0 || row >= table.length ? NaN : table[row]!;
  }
  return out;
}

/** The hemispheres' ranges as one, the lowest low to the highest high; the fallback where none says. */
export function rangeOf(ranges: (readonly [number, number] | undefined)[], fallback: [number, number]): [number, number] {
  const have = ranges.filter((r): r is readonly [number, number] => !!r && Number.isFinite(r[0]) && Number.isFinite(r[1]));
  if (!have.length) return fallback;
  return [Math.min(...have.map((r) => r[0])), Math.max(...have.map((r) => r[1]))];
}

/** Reads the target's map from `pack`, the cortex pack the viewport draws: both hemispheres in the pack's order (left, then right) as one value a vertex. */
export async function readMap(target: string, show: MapShow, pack: string, source: SimSource = packSource()): Promise<DensityMap> {
  const files = new SimFiles(source, pack);
  const structures = await files.maybeJson<{ id?: string; layers?: string }[]>('structures.json');
  const indexes = (structures ?? []).map((s) => s.layers).filter((l): l is string => typeof l === 'string');
  if (indexes.length !== 2) throw new Error(`${pack}: ${indexes.length} cortex surfaces, not two`);
  const name = layerFor(target, show);
  const parts: Float32Array[] = [];
  const ranges: ([number, number] | undefined)[] = [];
  let units = '';
  let method = '';
  let provenance = '';
  for (const index of indexes) {
    const ix = await files.maybeJson<LayerIndex>(index);
    const n = ix?.vertices ?? 0;
    if (!ix || !n) throw new Error(`${pack}: ${index} has no vertex count`);
    const entry = ix.layers?.find((l) => l.name === name);
    if (!entry) throw new NoMap(`${pack} has no ${name} layer for ${target}`);
    const data = decode(await files.bytes(entry.file), parseDType(entry.dtype));
    let values: Float32Array;
    if (entry.kind === 'parcel-values') {
      if (entry.index && entry.index !== 'label-1') throw new Error(`${name}: parcel index ${entry.index}, not label-1`);
      const lab = ix.layers?.find((l) => l.name === entry.label_layer && l.kind === 'label');
      if (!lab) throw new Error(`${name}: no ${entry.label_layer ?? ''} label layer on ${index}`);
      const labels = decode(await files.bytes(lab.file), parseDType(lab.dtype));
      if (labels.length !== n) throw new Error(`${lab.file}: ${labels.length} labels for ${n} vertices`);
      values = expandParcels(data, labels, Number(lab.nodata ?? 0));
    } else if (entry.kind === 'scalar') {
      if (data.length !== n) throw new Error(`${entry.file}: ${data.length} values for ${n} vertices`);
      values = data instanceof Float32Array ? data : Float32Array.from(data as ArrayLike<number>);
    } else throw new Error(`${name}: a ${entry.kind} layer isn't a map`);
    parts.push(values);
    ranges.push(entry.range);
    units ||= entry.units ?? '';
    method ||= entry.method ?? '';
    provenance ||= entry.provenance ?? '';
  }
  const values = new Float32Array(parts.reduce((k, p) => k + p.length, 0));
  let at = 0;
  for (const p of parts) {
    values.set(p, at);
    at += p.length;
  }
  let finite = 0;
  for (let i = 0; i < values.length; i++) if (Number.isFinite(values[i]!)) finite++;
  return { pack, layer: name, show, target, values, range: rangeOf(ranges, [0, 1]), units, method, provenance, finite, vertices: values.length };
}

export type DensityStatus = 'idle' | 'loading' | 'on' | 'none' | 'error';

interface DensityState {
  /** What the cortex shows for the picked receptor: the course's engagement, or the density alone (the parcel table, or the vertex-wise PET map). */
  show: DrugShow;
  setShow(show: DrugShow): void;
  status: DensityStatus;
  why: string | null;
  map: DensityMap | null;
  /** The viewport's reply to the layer, for the section and probes. */
  reply: { vertices: number; covered: number; of: number } | null;
  /** Pushes made, for probes. */
  sent: number;
}

export const useDrugDensity = create<DensityState>((set) => ({
  show: 'engagement',
  setShow: (show) => set({ show }),
  status: 'idle',
  why: null,
  map: null,
  reply: null,
  sent: 0,
}));

/** The live API (viewport-api-v0, Live vertex data), optional on today's viewports: the 2D preview has no `setVertexData`. */
interface LiveVp {
  send(cmd: Command): Promise<unknown>;
  setVertexData?(id: string, values: Float32Array, t?: number): void;
}

const reason = (e: unknown): string => (e instanceof Error ? e.message : String(e));
let gen = 0;

/** The layer's look: viridis over the map's own range, at full strength wherever it has a value (the tissue's colour where it hasn't), and
 *  the map's colour alone (`albedo: 1`): the HD cortex's own shade shows through a 70 % mix as a speckle that reads as data. */
export const densityLook = (map: Pick<DensityMap, 'range'>): { colormap: 'viridis'; range: [number, number]; rise: [number, number]; albedo: number } => ({ colormap: 'viridis', range: map.range, rise: [-1, 1], albedo: 1 });

/** What the section says when the pack lacks the map. */
export const noMapWhy = (target: string, show: MapShow): string =>
  show === 'pet' ? `The cortex’s pack has no vertex-wise PET map for ${target}; Density has Hansen 2022’s parcel means.` : `The cortex’s pack has no parcel table for ${target}.`;

async function sendMap(my: number, target: string, show: MapShow, pack: string): Promise<void> {
  useDrugDensity.setState({ status: 'loading', why: null });
  const vp = (await getViewport()) as LiveVp | null;
  if (!vp || my !== gen) return;
  if (typeof vp.setVertexData !== 'function') {
    useDrugDensity.setState({ status: 'none', why: 'This viewport doesn’t take live vertex data: the 2D preview draws the cortex without the map.', map: null, reply: null });
    return;
  }
  try {
    const map = await readMap(target, show, pack);
    if (my !== gen) return;
    const reply = (await vp.send({ cmd: 'vertexLayer', id: DENSITY_LAYER, surface: 'cortex', space: 'own', kind: 'scalar', look: densityLook(map) } as unknown as Command)) as
      | { vertices?: number; covered?: number; of?: number }
      | undefined;
    if (my !== gen) return;
    vp.setVertexData(DENSITY_LAYER, map.values);
    useDrugDensity.setState((s) => ({
      status: 'on',
      why: null,
      map,
      reply: reply && typeof reply.covered === 'number' ? { vertices: reply.vertices ?? 0, covered: reply.covered, of: reply.of ?? 0 } : null,
      sent: s.sent + 1,
    }));
  } catch (e) {
    if (my !== gen) return;
    useDrugDensity.setState({ status: e instanceof NoMap ? 'none' : 'error', why: e instanceof NoMap ? noMapWhy(target, show) : reason(e), map: null, reply: null });
  }
}

async function clearMap(): Promise<void> {
  ++gen;
  if (useDrugDensity.getState().status !== 'error') useDrugDensity.setState({ status: 'idle', why: null, map: null, reply: null });
  const vp = await getViewport();
  try {
    await vp?.send({ cmd: 'vertexLayer', id: DENSITY_LAYER, remove: true } as unknown as Command);
  } catch {
    // A viewport that never took the layer has nothing to forget.
  }
}

/** Drives the map while Drugs is on screen with its run, for the Show control's choice and the receptor picked, from the pack
 *  the viewport draws as the cortex. Mounted with the Drugs sections, which pass the target they show. */
export function useDrugDensityDrive(target: string | null): void {
  const on = useAtlas((s) => s.scenario === 'drugs' && !stillView(s));
  const ready = useAtlas((s) => s.engine.status === 'ready' && s.load.status === 'ready' && s.sims.scenario === s.scenario && s.sims.status === 'ready');
  const brainPack = useAtlas((s) => s.brain?.pack ?? null);
  const show = useDrugDensity((s) => s.show);
  useEffect(() => {
    if (!on || !ready || show === 'engagement' || !target) return;
    const my = ++gen;
    if (!brainPack) {
      useDrugDensity.setState({ status: 'none', why: 'This viewport draws no HD cortex over the pack, so there is no mesh to carry the map.', map: null, reply: null });
      return () => void clearMap();
    }
    void sendMap(my, target, show, brainPack);
    return () => void clearMap();
  }, [on, ready, show, target, brainPack]);
}

// The store for probes and the e2e run, as lifetime.ts exposes its own.
if (typeof window !== 'undefined') (window as unknown as { __drugDensity?: typeof useDrugDensity }).__drugDensity = useDrugDensity;
